import { Field, FieldProps } from 'formik';
import { FC } from 'react';
import { Conditional } from './Conditional';
import { Validations, gen_validate } from '../../helper/validation';

export enum InputMode {
  Text = 'text',
  Number = 'numeric',
  Email = 'email',
  PhoneNumber = 'tel',
}

interface UserInputProps {
  name: string;
  prompt?: string;
  placeholder?: string;
  extraInformation?: string;
  inputMode?: InputMode;
  validations?: Validations<string>;
  maxLength?: number;
  multiLine?: boolean;
}

export const UserInput: FC<UserInputProps> = ({
  name,
  prompt,
  placeholder,
  extraInformation,
  inputMode = InputMode.Text,
  validations,
  maxLength,
  multiLine,
}) => {
  const getBorderStyling = (hasErrors: boolean) => {
    if (hasErrors) {
      return 'text-sami-red border-sami-red border-opacity-100 border-b-2 border-t-0 border-x-0 hover:text-sami-red hover:bg-sami-red-lighter hover:border-sami-red';
    } else {
      return 'text-text-gray border-input-border hover:text-sami-green hover:bg-sami-hover hover:border-sami-green';
    }
  };

  return (
    <Field name={name} validate={gen_validate(validations)}>
      {({ field, meta }: FieldProps<string>) => (
        <div className="w-full">
          <div className="flex flex-col mb-2 text-sm font-bold text-text-dark">{prompt}</div>

          {multiLine ? (
            <textarea
              rows={3}
              cols={40}
              maxLength={800}
              placeholder={placeholder}
              className={`w-full min-h-[130px] sm:min-h-[100px] py-2 sm:py-3 px-3 sm:pl-6 sm:pr-4 align-top
            rounded-md bg-input-gray border border-input-border focus:outline-none ${
              meta.error
                ? 'text-red-darker border-sami-red focus:border-sami-red'
                : 'text-text-dark focus:border-sami-green'
            }`}
              {...field}
            />
          ) : (
            <div
              className={`flex flex-row items-center overflow-hidden bg-input-gray rounded-lg border border-opacity-20 hover:border-b-2 hover:border-t-0 hover:border-x-0 ${getBorderStyling(
                !!meta.error && (meta.touched || !!meta.value),
              )}`}
            >
              <input
                type="text"
                placeholder={placeholder}
                inputMode={inputMode}
                className={`w-full py-3 pl-6 pr-3 bg-inherit text-base ${
                  meta.error ? 'text-red-darker' : 'text-text-dark'
                } focus:outline-none overflow-hidden text-ellipsis`}
                {...field}
                maxLength={maxLength}
              />
            </div>
          )}

          {extraInformation && <div className="self-start mt-3 text-xs text-text-gray">{extraInformation}</div>}
          <div className="pl-4 mt-1 min-h-5">
            <Conditional
              condition={!!meta.error && (meta.touched || !!meta.value)}
              trueRender={<div className="text-sm font-medium text-sami-red">{meta.error}</div>}
            />
          </div>
        </div>
      )}
    </Field>
  );
};
